// Konfiguration — "hvad er sat op i jeres Microsoft 365, på tværs?" One tile per Microsoft service; under it two
// sections: Indstillinger (what NordComply has read and assessed, as cards by status: Skal rettes, Bør ses på, I orden)
// and Politikker (what the customer or their IT supplier has created, by type, not assessed). The same design as Gennemgang:
// cards on the left, the chosen row in the side panel, and decisions made in the decision window. Whatever is decided here
// lands under Beslutninger. Status against frameworks is Compliance's job, not this page's.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { useToolsAlign } from '../hooks/useToolsAlign';
import { useTenantData } from '../context/TenantDataContext';
import { useAuth } from '../context/AuthContext';
import { useCachedFetch } from '../hooks/useCachedFetch';
import { apiFetch } from '../api';
import LoadingState from '../components/LoadingState';
import NcIcon from '../components/NcIcon';
import PortalIcon from '../components/PortalIcon';
import SearchInput from '../components/SearchInput';
import { Group, RowButton } from '../components/DetailPanel';
import ExpandAll from '../components/ExpandAll';
import StatusFrame from '../components/StatusFrame';
import FocusToggle, { useFocusMode } from '../components/FocusToggle';
import { useItList } from '../hooks/useItList';
import { cardItProps, itKeyOf } from '../utils/itCard';
import DecisionTag from '../components/DecisionTag';
import { DECISION_LABEL } from '../components/DecisionTypeRadios';
import { TileMenu, StatusCard, OnePagePanel } from '../components/StatusCards';
import { getStatusBadge } from '../utils/statusBadge';
import { decisionsByItem } from '../utils/decisions';
import { AREA_LABEL, AREA_ICON } from '../data/areaLabels';
import { CONFIG_AREAS, buildConfig, configStats } from '../data/configModel';
import { checkedAgo } from '../data/exchangeSettings';
import { CANNOT_FETCH, assignmentText, shortDate } from '../utils/policyDisplay';
import { STATUS_LABEL, entrySummary, listValue, policyKey } from '../utils/policyResults';
import './styles.css';
import './WorkQueueGroups.css';
import './ConfigurationPage.css';

// Which /api/policy-inventory categories belong under which Konfiguration area — so the raw Intune/
// Exchange policy objects (which we never evaluate a tone for, only the settings we've read out of
// them) get a real place to be opened and read, next to the settings we DO evaluate. Areas not listed
// here have no Intune-style inventory of their own (identity, security, sharepoint, roles, CA policies
// come from other Graph endpoints, not this inventory).
const AREA_POLICY_CATEGORIES = {
  devices: ['compliance', 'configuration', 'settingsCatalog', 'endpointSecurity', 'adminTemplate', 'update', 'autopilot', 'enrollment'],
  applications: ['appProtection', 'appConfig'],
  exchange: ['mail'],
};
// Politikker are told apart by where they come from and where an administrator changes them, not by what kind of
// object they are: an anti-phishing policy is read through Exchange but edited in the Defender portal.
const POLICY_SOURCES = [
  { key: 'entra', label: 'Entra ID', from: 'Microsoft Entra ID', editIn: 'Entra admin center' },
  { key: 'intune', label: 'Intune', from: 'Microsoft Intune', editIn: 'Intune admin center' },
  { key: 'sharepoint', label: 'SharePoint og Teams', from: 'SharePoint Online og Microsoft Teams', editIn: 'SharePoint admin center og Teams admin center' },
  { key: 'exchange', label: 'Exchange Online', from: 'Exchange Online', editIn: 'Exchange admin center' },
  { key: 'defender', label: 'Defender', from: 'Defender for Office 365 (læses via Exchange Online)', editIn: 'Defender-portalen (security.microsoft.com)' },
  { key: 'purview', label: 'Purview', from: 'Microsoft Purview', editIn: 'Purview-portalen' },
];
// Which service each area of assessed settings belongs to. Conditional Access is the one exception to "settings":
// its rules are policies, so they are listed under Entra ID's Politikker.
const SERVICE_OF_AREA = {
  devices: 'intune', applications: 'entra', identity: 'entra', roles: 'entra', 'conditional-access': 'entra',
  exchange: 'exchange', security: 'defender', sharepoint: 'sharepoint',
};
const CA_AREA = 'conditional-access';
// One line icon and one colour per service (our own drawings, not Microsoft's marks).
const SERVICE_STYLE = {
  intune: { bg: '#E7F0FF', fg: '#2D63C8', icon: <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></> },
  exchange: { bg: '#E3F4EC', fg: '#177A5B', icon: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></> },
  defender: { bg: '#FBEEDD', fg: '#EE8A00', icon: <path d="M12 3l8 3v6c0 4.5-3.2 8-8 9-4.8-1-8-4.5-8-9V6z" /> },
  entra: { bg: '#EEEDFD', fg: '#4446C6', icon: <><circle cx="12" cy="8" r="4" /><path d="M4 21c1-4 4-6 8-6s7 2 8 6" /></> },
  sharepoint: { bg: '#E0F2F4', fg: '#0E7C86', icon: <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /> },
  purview: { bg: '#EDEAF3', fg: '#7A6FA0', icon: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h3" /></> },
};
const SOURCE_BY_KEY = Object.fromEntries(POLICY_SOURCES.map((s) => [s.key, s]));
// Mail policies are split by what they are, and by which portal owns them.
const MAIL_GROUPS = {
  HostedContentFilterPolicy: { gkey: 'mail-spam-in', label: 'Spamfilter, indgående', source: 'exchange' },
  HostedOutboundSpamFilterPolicy: { gkey: 'mail-spam-out', label: 'Spamfilter, udgående', source: 'exchange' },
  TransportRule: { gkey: 'mail-rules', label: 'Mailregler', source: 'exchange' },
  DkimSigningConfig: { gkey: 'mail-dkim', label: 'DKIM', source: 'exchange' },
  SafeLinksPolicy: { gkey: 'mail-safelinks', label: 'Safe Links', source: 'defender' },
  SafeAttachmentPolicy: { gkey: 'mail-safeattach', label: 'Safe Attachments', source: 'defender' },
  AntiPhishPolicy: { gkey: 'mail-antiphish', label: 'Anti-phishing', source: 'defender' },
};
// What an enrollment or Autopilot object is. Intune gives the default ones the same name ("All users and all devices"),
// so the kind is what tells them apart.
const TYPE_LABEL = {
  deviceEnrollmentPlatformRestrictionsConfiguration: 'Platformsbegrænsninger',
  deviceEnrollmentPlatformRestrictionConfiguration: 'Platformsbegrænsning',
  deviceEnrollmentLimitConfiguration: 'Grænse for antal enheder pr. bruger',
  deviceEnrollmentWindowsHelloForBusinessConfiguration: 'Windows Hello for Business ved tilmelding',
  windows10EnrollmentCompletionPageConfiguration: 'Enrollment Status Page',
  windowsRestoreDeviceEnrollmentConfiguration: 'Gendannelse af Windows-enhed',
  azureADWindowsAutopilotDeploymentProfile: 'Autopilot-profil',
  activeDirectoryWindowsAutopilotDeploymentProfile: 'Autopilot-profil (hybrid)',
};
const DEFAULT_NAME = /^all users and all devices$/i;
const policyTitle = (item) => (DEFAULT_NAME.test(item.name || '') && TYPE_LABEL[item.type] ? `${TYPE_LABEL[item.type]} (standard)` : item.name);
const policySub = (item) => [TYPE_LABEL[item.type] && !DEFAULT_NAME.test(item.name || '') ? TYPE_LABEL[item.type] : item.platform, item.assignmentLabel].filter(Boolean).join(' · ') || '—';

// One inventory category becomes one group, except mail (one group per policy type) and enrollment (platform restrictions apart).
function splitPolicyGroup(g) {
  if (g.key === 'enrollment') {
    const platform = g.items.filter((i) => /PlatformRestriction/i.test(i.type || ''));
    const rest = g.items.filter((i) => !/PlatformRestriction/i.test(i.type || ''));
    return [
      ...(platform.length ? [{ ...g, category: g.key, gkey: 'enroll-platform', label: 'Platformsbegrænsninger (hvilke enheder må tilmeldes)', source: 'intune', items: platform }] : []),
      ...(rest.length ? [{ ...g, category: g.key, gkey: 'enroll-other', label: 'Tilmelding og Enrollment Status Page', source: 'intune', items: rest }] : []),
    ];
  }
  if (g.key !== 'mail') return [{ ...g, category: g.key, gkey: g.key, source: 'intune' }];
  const byType = new Map();
  g.items.forEach((item) => {
    const meta = MAIL_GROUPS[item.type] || { gkey: 'mail-other', label: 'Andre mailpolitikker', source: 'exchange' };
    if (!byType.has(meta.gkey)) byType.set(meta.gkey, { ...g, category: g.key, gkey: meta.gkey, label: meta.label, source: meta.source, items: [] });
    byType.get(meta.gkey).items.push(item);
  });
  return [...byType.values()];
}
// A policy has no rating of its own, but when its result is known the row says how many devices have it in
// order ("82/90"), in red when some do not. Policies without a result source show nothing there.
function policyRow(item, category, results) {
  const v = results ? listValue(results.policies?.[policyKey(category, item.id)]) : null;
  return { id: `pol:${category}:${item.id}`, title: policyTitle(item), sub: null, tone: null, value: v ? v.text : '', valueTone: v ? v.tone : null, kind: 'policy', item };
}
// The colour of a policy in a list. Green only when the policy is assigned and devices report on it with nothing wrong;
// grey when it exists but is not assigned, or no device reports on it (yet); orange or red when devices fail it.
export const isAssigned = (item) => !!item.assignment && item.assignment.state !== 'none';
export function policyMark(item, res) {
  if (!isAssigned(item) || !res || res.total === 0) return 'unknown';
  if (res.problems === 0) return 'covered';
  return res.problems / Math.max(res.total, 1) > 0.15 ? 'notFound' : 'partial';
}
const rated = (tone) => tone === 'ok' || tone === 'warn' || tone === 'bad';
// A boolean-ish fact value ("Ja"/"Nej") gets the same small pill DecisionTag-style badges use
// elsewhere, instead of sitting as bare text next to its label — everything else stays plain mono.
function factValue(v) {
  if (v === 'Ja') return <span className="fact-pill fact-pill--yes">Ja</span>;
  if (v === 'Nej') return <span className="fact-pill fact-pill--no">Nej</span>;
  return v;
}


/* ---------- the chosen row ---------- */
const policyDetailCache = new Map();

// One Intune/Exchange policy's own settings, fetched when the row is chosen (shared cache, so choosing a row twice
// never asks Microsoft twice). item is null for tenant settings, which have nothing to fetch.
function usePolicyDetail(item, tenantId) {
  const key = item ? `${tenantId}:${item.category}:${item.id}` : null;
  const [state, setState] = useState(() => (key && policyDetailCache.has(key) ? { data: policyDetailCache.get(key) } : { loading: !!key }));
  useEffect(() => {
    if (!key) return undefined;
    if (policyDetailCache.has(key)) { setState({ data: policyDetailCache.get(key) }); return undefined; }
    setState({ loading: true });
    let cancelled = false;
    const params = new URLSearchParams({ tenantId, category: item.category, id: item.id, type: item.type || '', platform: item.platform || '' });
    apiFetch(`/api/policy-detail?${params}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (!data.error) policyDetailCache.set(key, data);
        setState({ data });
      })
      .catch(() => !cancelled && setState({ data: { error: CANNOT_FETCH } }));
    return () => { cancelled = true; };
  }, [key, item, tenantId]);
  return state;
}

// What the devices report for one policy. Only the fact that something fails is shown, never why (that is Intune's to
// explain). Only some policy types have a result source at all.
// Enrollment rules and Autopilot profiles have no result per device in Intune, so they never get one.
const DEVICE_POLICY_CATEGORIES = ['compliance', 'configuration', 'settingsCatalog', 'endpointSecurity', 'adminTemplate', 'update'];
function resultInfo(item, results) {
  if (!DEVICE_POLICY_CATEGORIES.includes(item.category)) return { kind: 'unsupported' };
  if (results.loading) return { kind: 'loading' };
  const data = results.data && !results.data.error ? results.data : null;
  const entry = data?.policies?.[policyKey(item.category, item.id)];
  if (!entry) return { kind: 'missing', reason: data?.unavailable?.[item.category] };
  const s = entrySummary(entry);
  if (s.total === 0) return { kind: 'empty' };
  return { kind: 'ok', s, entry };
}

// The devices behind a policy's result, in two folds: the ones that fail (open) and the ones in order (closed).
// A device row is itself the button that opens it in Gennemgang.
function PolicyResult({ info, tenantId }) {
  const navigate = useNavigate();
  if (info.kind === 'unsupported') return null;
  if (info.kind === 'loading') return <p className="dp-muted">Henter resultat…</p>;
  if (info.kind === 'missing') return <p className="dp-muted dp-gap">Resultat: Ikke tilgængeligt endnu{info.reason ? ` (${info.reason.toLowerCase()})` : ''}</p>;
  if (info.kind === 'empty') return <p className="dp-muted">Politikken rammer ingen enheder endnu.</p>;
  const { s, entry } = info;
  const problemDevices = entry.devices.filter((d) => d.status === 'nonCompliant' || d.status === 'error' || d.status === 'conflict');
  const okDevices = entry.devices.filter((d) => d.status === 'compliant');
  // Gennemgang opens the device in its normal view (Prioritet, Enheder) with the device chosen in the side panel.
  const openDevice = (id) => navigate(`/tenant/${tenantId}/work-queue?area=devices&device=${encodeURIComponent(id)}`);
  return (
    <>
      {problemDevices.length === 0 ? (
        <p className="pr-okbox">{s.pending ? `Ingen fejl eller konflikter. ${s.pending} ${s.pending === 1 ? 'enhed afventer' : 'enheder afventer'} stadig, og det er normalt kortvarigt.` : `Ingen fejl eller konflikter. Alle ${s.total} enheder opfylder politikken.`}</p>
      ) : (
        <Group label="Skal rettes" count={problemDevices.length} tone="bad">
          {problemDevices.map((d) => (
            <RowButton key={`${d.id}-${d.name}`} title={d.name || 'Ukendt enhed'} sub={d.user || '—'} pill={{ tone: 'bad', text: STATUS_LABEL[d.status] }} onClick={d.id ? () => openDevice(d.id) : undefined} />
          ))}
        </Group>
      )}
      <Group label="Opfyldt" count={s.ok} tone="ok" limit={problemDevices.length === 0 ? 8 : 3}>
        {okDevices.map((d) => <RowButton key={`${d.id}-${d.name}`} title={d.name || 'Ukendt enhed'} pill={{ tone: 'ok', text: 'Opfyldt' }} />)}
        {entry.truncated && <p className="dp-muted" style={{ padding: '8px' }}>Kun de første {okDevices.length} opfyldte enheder vises. Tallet ovenfor er det fulde antal.</p>}
      </Group>
    </>
  );
}

// The setting's own evidence rows, labels in Danish; the source and time go in the footer instead.
const EV_LABEL = { 'Observed value': 'Observeret værdi', State: 'Tilstand', 'Schema version': 'Skema-version' };
const settingEvidence = (row) => (row.evidence || [])
  .filter(([k, v]) => !['Source', 'Collected at'].includes(k) && v !== undefined && v !== null && v !== '')
  .map(([k, v]) => [EV_LABEL[k] || k, String(v)]);

// A Conditional Access rule in plain words, as a short flow: who it covers, on which apps, from where, and what it requires.
const CA_STATE_TEXT = { enabled: 'Aktiveret', disabled: 'Deaktiveret', enabledForReportingButNotEnforced: 'Kun rapportering' };
function caRequires(p) {
  const c = p.controlsShort || [];
  if (c.length === 0) return 'Intet krav';
  if (c.includes('Blokeret')) return 'Blokerer adgang';
  const or = p.grantControls?.operator === 'OR';
  return `Kræver ${or && c.length > 1 ? 'ét af: ' : ''}${c.join(or ? ', ' : ' + ')}`;
}
// Information rows that still get a recommendation in the side panel.
const ADVICE_FOR_INFO = new Set(['Session-timeout-politik', 'Navngivne lokationer', 'Adgangskode-udløb', 'Rolletildelinger i alt']);

function caSteps(p) {
  const platforms = (p.conditions?.platforms || []).filter((x) => x !== 'all');
  const risk = [...(p.riskLevels?.signIn || []), ...(p.riskLevels?.user || [])];
  const clients = (p.clientAppTypes || []).filter((x) => !/^alle/i.test(x));
  return [
    ['Gælder for', p.targetSummaryShort || p.includeUsers || 'Alle brugere', p.excludeSummaryShort || p.excludeUsers],
    ['På', p.includeApps || 'Alle apps', p.excludeApps],
    ['Fra', p.locationsInclude || 'Alle lokationer', p.locationsExclude],
    ['Kun på platforme', platforms.length ? platforms.join(', ') : null],
    ['Klienter', clients.length ? clients.join(', ') : null],
    ['Enheder', p.deviceFilter?.rule ? `${p.deviceFilter.mode === 'exclude' ? 'Ikke' : 'Kun'}: ${p.deviceFilter.rule}` : null],
    ['Ved risiko', risk.length ? risk.join(', ') : null],
    ['Handling', (p.userActions || []).length ? p.userActions.join(', ') : null],
    ['Login-flow', (p.authFlows || []).length ? p.authFlows.join(', ') : null],
    ['Så', caRequires(p), null, true],
    ['Session', (p.sessionControlsSummary || []).length ? p.sessionControlsSummary.join(', ') : null],
  ].filter((s) => s[1]);
}
// The full names behind the short lines in the list: which roles, users and groups, which apps and which places.
function caEvidence(p) {
  const names = (list, fallback) => (list && list.length ? list.join(', ') : fallback);
  return [
    ['Gælder for', names(p.includeUsersList, p.includeUsers || 'Alle brugere')],
    ...(p.excludeUsersList?.length ? [['Undtaget', names(p.excludeUsersList)]] : []),
    ['Apps', names(p.includeAppsList, p.includeApps || 'Alle apps')],
    ...(p.excludeAppsList?.length ? [['Apps undtaget', names(p.excludeAppsList)]] : []),
    ['Lokationer', p.locationsInclude || 'Alle lokationer'],
    ...(p.locationsExclude ? [['Lokationer undtaget', p.locationsExclude]] : []),
    ['Krav', caRequires(p)],
    ['Tilstand', CA_STATE_TEXT[p.state] || p.state || '—'],
    ...(p.modifiedDateTime ? [['Sidst ændret', shortDate(p.modifiedDateTime)]] : []),
  ];
}
function CaFlow({ p }) {
  return (
    <ol className="caflow">
      {caSteps(p).map(([label, text, excl, last], i) => (
        <li key={label} className={last ? 'is-last' : undefined}>
          <span className="caflow__n">{i + 1}</span>
          <div><small>{label}</small><b>{text}</b>{excl && <em>Undtaget: {excl}</em>}</div>
        </li>
      ))}
    </ol>
  );
}

const TONE_TEXT = { bad: ['Skal rettes', 'r'], warn: ['Bør ses på', 'o'], ok: ['I orden', 'g'] };
const FOLDED_BY_DEFAULT = ['ok', 'neutral'];
const NOUN = { set: ['indstilling', 'indstillinger'], ca: ['regel', 'regler'], pol: ['politik', 'politikker'] };
const countText = (n, [one, many]) => `${n} ${n === 1 ? one : many}`;
// Each section has its own colour: the pill and the dot in the menu share it.
const FUND_ROWS = 4;
const areaOfCategory = (category) => (Object.entries(AREA_POLICY_CATEGORIES).find(([, keys]) => keys.includes(category)) || [])[0] || 'devices';

const proseCard = (key, title, text) => (text ? {
  key, title, count: null, summary: text, body: <div className="pn-pad"><p className="pn-prose">{text}</p></div>,
} : null);
const factsCard = (key, title, rows) => (rows.length ? {
  key,
  title,
  count: rows.length,
  summary: `${rows[0][1] && typeof rows[0][1] === 'string' ? rows[0][1] : ''}${rows.length > 1 && typeof rows[0][1] === 'string' ? ` · +${rows.length - 1} mere` : ''}`,
  body: (
    <div className="pn-list">
      {rows.map(([label, value, mono]) => <div className="pn-row" key={label}><span>{label}</span><b className={mono ? 'pn-mono' : undefined}>{value}</b></div>)}
    </div>
  ),
} : null);

// The side panel for one row: a setting (assessed) or a policy (not assessed, but what devices report is shown).
function RowPanel({ row, area, tenant, groups, results, evidenceFor, note }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPolicy = row.kind === 'policy';
  const detailState = usePolicyDetail(isPolicy ? row.item : null, tenant.tenantId);
  const detail = detailState.data && !detailState.data.error ? detailState.data : null;
  const info = isPolicy ? resultInfo(row.item, results) : null;

  let tiles;
  let box = null;
  let warn = null;
  const cards = [];
  if (!isPolicy) {
    const tone = rated(row.tone) ? row.tone : 'neutral';
    const [stText, stKey] = TONE_TEXT[tone] || ['Oplysning', 'n'];
    tiles = row.ca
      ? [['Status', row.ca.state === 'enabled' ? 'Aktiveret' : stText, row.ca.state === 'enabled' ? 'g' : stKey], ['Gælder for', row.ca.targetSummaryShort || row.ca.includeUsers || 'Alle brugere', 'n'], ['Undtaget', row.ca.excludeSummaryShort || row.ca.excludeUsers || 'Ingen', row.ca.excludeUsers ? 'o' : 'n']]
      : [['Status', stText, stKey], ['Område', AREA_LABEL[area] || area, 'n'], ['Værdi', row.value, 'n']];
    // An information row (no rating) still carries a recommendation, for the few that have one.
    if (row.recommend && (tone !== 'neutral' || ADVICE_FOR_INFO.has(row.title))) box = { tone: tone === 'neutral' ? 'ok' : tone, title: 'Anbefaling', text: row.recommend };
    if (row.ca) cards.push({ key: 'flow', title: 'Sådan virker reglen', count: null, summary: caRequires(row.ca), open: true, body: <div className="pn-pad"><CaFlow p={row.ca} /></div> });
    cards.push(
      proseCard('what', 'Hvad er det', row.meaning),
      row.ca ? null : proseCard('sit', 'Situationen hos jer', row.situation || `${row.title}: ${row.value}`),
      factsCard('ev', 'Evidens', row.ca ? caEvidence(row.ca) : [...settingEvidence(row), ...(row.record ? [['Post', row.record, true]] : [])]),
    );
  } else {
    const it = row.item;
    const assigned = isAssigned(it);
    const result = !assigned ? 'Ikke tildelt'
      : info.kind === 'ok' ? `${info.s.ok} af ${info.s.total} enheder`
        : info.kind === 'loading' ? 'Henter…'
          : info.kind === 'empty' ? 'Ingen enheder endnu'
            : info.kind === 'unsupported' ? 'Ikke målt pr. enhed' : 'Ikke tilgængeligt endnu';
    tiles = [['Type', 'Politik', 'n'], ['Tildelt', assigned ? 'Ja' : 'Nej', assigned ? 'g' : 'n'], ['Resultat', result, info.kind === 'ok' && info.s.problems > 0 ? 'r' : 'n']];
    if (info.kind === 'ok' && info.s.problems > 0) {
      box = { tone: 'bad', title: 'Anbefaling', text: 'Ret de enheder, der fejler.' };
      warn = { text: `${info.s.problems} af ${info.s.total} enheder opfylder ikke politikken.`, cons: 'Enheder, der ikke lever op til politikken, kan have adgang til jeres data uden den beskyttelse, I forventer.' };
      // The result now stands in the red box, so the grey line under the title does not repeat it.
      tiles = tiles.filter(([label]) => label !== 'Resultat');
    }
    const rows = detail?.rows || [];
    cards.push(
      info.kind !== 'unsupported' ? { key: 'res', title: 'Resultat', count: info.kind === 'ok' ? info.s.total : null, summary: result, body: <div className="pn-pad"><PolicyResult info={info} tenantId={tenant.tenantId} /></div> } : null,
      proseCard('what', 'Hvad er det', detail?.summary),
      proseCard('sit', 'Situationen hos jer', `Tildelt: ${assignmentText(it, groups)}.${it.lastModifiedDateTime ? ` Sidst ændret ${shortDate(it.lastModifiedDateTime)}.` : ''}`),
      detailState.loading ? { key: 'ev', title: 'Evidens', count: null, summary: 'Henter politikken…', body: <p className="dp-muted">Henter politikken…</p> }
        : detailState.data?.error ? { key: 'ev', title: 'Evidens', count: null, summary: CANNOT_FETCH, body: <p className="dp-muted">{CANNOT_FETCH}</p> }
          : factsCard('ev', 'Evidens', [['Platform', it.platform || '—'], ...rows.map((r) => [r.label, factValue(r.value)]), ['Politik-ID', it.id, true]]),
    );
  }
  const decidedLabel = note?.decisionType ? DECISION_LABEL[note.decisionType] : null;
  const who = note && (note.authorName && note.authorName === user?.name ? 'Du' : note.authorName);
  return (
    <OnePagePanel
      banner={evidenceFor && (
        // Arrived from a requirement in Compliance: the policy is shown as that requirement's evidence, not as an
        // assessment of it. Compliance holds the assessment; this panel only holds the policy.
        <div className="pr-from">
          <span>Vist som evidens for kravet <b>{evidenceFor}</b>. Her er det politikken, ikke en vurdering af kravet.</span>
          <button type="button" className="cmp-link" onClick={() => navigate(`/tenant/${tenant.tenantId}/compliance`)}>Tilbage til Compliance</button>
        </div>
      )}
      title={row.title}
      sub={[AREA_LABEL[area] || area, isPolicy ? policySub(row.item) : row.sub].filter((x) => x && x !== '—').join(' · ')}
      tiles={tiles}
      warn={warn}
      note={box}
      cards={cards.filter(Boolean)}
      decision={{ tag: decidedLabel, tagKey: note?.decisionType, meta: note ? [who, new Date(note.createdAt).toLocaleDateString('da-DK')].filter(Boolean).join(' · ') : '', label: '', onOpen: undefined }}
    />
  );
}

const STATUS_FILTERS = [['all', 'Alle'], ['bad', 'Skal rettes'], ['warn', 'Bør ses på'], ['ok', 'I orden']];

export default function ConfigurationPage() {
  const { tenant, loading, mfa, ca, applications, sharePointSettings, devices, membership, resources } = useTenantData();
  const itList = useItList(tenant?.tenantId);
  const [searchParams] = useSearchParams();
  // One tile per Microsoft service. Under each, Indstillinger (what NordComply has assessed) and Politikker (what the
  // customer has set up) are two sections, so a service is read in one place.
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [selId, setSelId] = useState(null);
  const detailPanel = useResizablePanel(528, 340, 1000);
  const [pageRef, toolsStyle] = useToolsAlign();
  // A card is folded or open by what the user last chose; "I orden" starts folded, the rest open.
  const [fold, setFold] = useState({});
  // Indstillinger and Politikker are two tabs, one shown at a time (so no section has to be folded).
  const [secTab, setSecTab] = useState('settings');
  const [showAll, setShowAll] = useState(() => new Set());
  const [picked, setPicked] = useState(() => new Set());
  // Fokus: while it is on, the cards you are not working in step back. It can be switched off for good (remembered in
  // this browser, the same switch as Gennemgang's).
  const [focusOn, toggleFocus] = useFocusMode();
  const [frameFold, setFrameFold] = useState(() => new Set());
    // Which Microsoft service is open (see POLICY_SOURCES). A link with ?area= or ?policy= switches it.
  const [pArea, setPArea] = useState(() => {
    const area = searchParams.get('area');
    return (area && SERVICE_OF_AREA[area]) || 'entra';
  });
  // The status filter speaks about settings; for policies it means "something to fix" and "all fine".
  const pStatus = status === 'bad' ? 'bad' : status === 'ok' ? 'ok' : status === 'warn' ? 'none' : 'all';
  const tid = tenant?.tenantId;
  const inv = useCachedFetch(tenant ? `/api/policy-inventory?tenantId=${tid}` : null, { skip: !tenant });
  const scope = useCachedFetch(tenant ? `/api/policy-scope?tenantId=${tid}` : null, { skip: !tenant });
  const results = useCachedFetch(tenant ? `/api/policy-results?tenantId=${tid}` : null, { skip: !tenant });
  const resultData = results.data && !results.data.error ? results.data : null;
  const notesReq = useCachedFetch(tenant ? `/api/audit-notes?tenantId=${tid}` : null, { skip: !tenant });
  const decided = useMemo(() => decisionsByItem(notesReq.data?.notes, 'cfg:'), [notesReq.data]);
  // Same routes Gennemgang already reads — shared through useCachedFetch, never a second ask to Microsoft.
  const { data: risks } = useCachedFetch(tenant ? `/api/identity-risks?tenantId=${tid}` : null, { skip: !tenant });
  const { data: sitesResult } = useCachedFetch(tenant ? `/api/sharepoint-sites?tenantId=${tid}` : null, { skip: !tenant });
  const { data: groupsResult } = useCachedFetch(tenant ? `/api/groups?tenantId=${tid}` : null, { skip: !tenant });
  const { data: insightsData } = useCachedFetch(tenant ? `/api/exchange-insights?tenantId=${tid}` : null, { skip: !tenant });

  const config = useMemo(() => {
    if (!tenant) return null;
    // Each area's settings keep buildConfig's own order (the setting's natural place in that area,
    // not sorted by how bad it is) — an inventory reads by subject, not by severity.
    return buildConfig({
      mfa, ca, applications, sharePointSettings, devices, resources, membership,
      risks: risks?.error ? null : risks,
      insights: insightsData?.error ? null : insightsData,
      inv: inv.data,
      scope,
      sites: sitesResult?.sites,
      workspaces: groupsResult?.workspaces,
      checkedAt: checkedAgo(tenant.lastCheckedAt),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, mfa, ca, applications, sharePointSettings, devices, resources, membership, risks, insightsData, inv.data, scope.data, sitesResult, groupsResult]);

  // Raw Intune/Exchange policy objects, grouped by category, per area — see AREA_POLICY_CATEGORIES.
  // Not evaluated (no tone), just a real place to open one and read what's actually in it.
  const policyGroups = useMemo(() => {
    const cats = inv.data?.categories;
    const out = {};
    if (!cats) return out;
    Object.entries(AREA_POLICY_CATEGORIES).forEach(([area, keys]) => {
      const groups = keys
        .map((key) => ({ key, cat: cats[key] }))
        .filter(({ cat }) => cat && cat.items && cat.items.length > 0)
        .map(({ key, cat }) => ({ key, label: cat.label, items: cat.items }));
      if (groups.length > 0) out[area] = groups;
    });
    return out;
  }, [inv.data]);
  // Every row that can be chosen, whether or not its card is open right now.
  const rowsById = useMemo(() => {
    const map = new Map();
    if (config) CONFIG_AREAS.forEach((a) => {
      config[a].settings.forEach((r) => map.set(r.id, { row: r, area: a }));
    });
    Object.entries(policyGroups).forEach(([area, groups]) => {
      groups.forEach((g) => g.items.forEach((item) => {
        const row = policyRow(item, g.key, resultData);
        map.set(row.id, { row, area });
      }));
    });
    return map;
  }, [config, policyGroups, resultData]);

  const q = search.trim().toLowerCase();
  const sections = useMemo(() => {
    if (!config) return [];
    return CONFIG_AREAS.map((area) => {
      const settings = config[area].settings.filter((r) => (status === 'all' || r.tone === status)
        && (!q || `${r.title} ${r.sub || ''} ${r.value}`.toLowerCase().includes(q)));
      // Policies aren't rated, so the status filter doesn't apply to them — a policy stays visible regardless,
      // search still narrows it.
      const policies = (policyGroups[area] || [])
        .map((g) => ({ ...g, items: g.items.filter((it) => !q || it.name.toLowerCase().includes(q)) }))
        .filter((g) => g.items.length > 0);
      return { area, settings, policies, stats: configStats(config[area].settings) };
    }).filter((s) => s.settings.length > 0 || s.policies.length > 0);
  }, [config, policyGroups, status, q]);

  // Every policy group, with the service (Intune, Exchange Online, Defender) it belongs to, and each policy carrying its result.
  const allPolicyGroups = useMemo(() => sections.flatMap((s) => s.policies).flatMap(splitPolicyGroup), [sections]);
  const policyTypes = useMemo(() => {
    return allPolicyGroups.filter((g) => g.source === pArea).map((g) => {
      const rows = g.items.map((item) => {
        const entry = resultData?.policies?.[policyKey(g.category, item.id)];
        return { id: `pol:${g.category}:${item.id}`, item, res: entrySummary(entry) };
      }).filter((r) => pStatus === 'all' || (pStatus === 'bad' ? !!r.res && r.res.problems > 0 : pStatus === 'ok' ? !!r.res && r.res.problems === 0 : false));
      // Worst first: the policies most devices fail, then the ones with a result, then the rest.
      rows.sort((a, b) => ((b.res?.problems || 0) - (a.res?.problems || 0)) || (Number(!!b.res) - Number(!!a.res)));
      return { key: g.gkey, category: g.category, label: g.label, rows, problems: rows.reduce((n, r) => n + (r.res ? r.res.problems : 0), 0), known: rows.some((r) => r.res) };
    }).filter((t) => t.rows.length > 0).map((t, i) => [t, i]).sort(([a, i], [b, j]) => (b.problems - a.problems) || (Number(b.known) - Number(a.known)) || i - j).map(([t]) => t);
  }, [allPolicyGroups, resultData, pStatus, pArea]);

  // What each service holds, for the tiles: how many of its assessed settings should be fixed or looked at.
  const tileStatus = useMemo(() => {
    const out = Object.fromEntries(POLICY_SOURCES.map((s) => [s.key, { ok: 0, warn: 0, bad: 0 }]));
    if (config) CONFIG_AREAS.forEach((a) => {
      const svc = SERVICE_OF_AREA[a];
      if (!svc) return;
      const st = configStats(config[a].settings);
      out[svc].ok += st.ok;
      out[svc].warn += st.warn;
      out[svc].bad += st.bad;
    });
    // The policies count too, not only the settings: a device policy that fails is something to act on, so the menu and the page agree.
    Object.values(policyGroups).flat().flatMap(splitPolicyGroup).forEach((g) => {
      if (!out[g.source]) return;
      g.items.forEach((item) => {
        const mark = policyMark(item, entrySummary(resultData?.policies?.[policyKey(g.category, item.id)]));
        if (mark === 'notFound') out[g.source].bad += 1;
        else if (mark === 'partial') out[g.source].warn += 1;
        else if (mark === 'covered') out[g.source].ok += 1;
      });
    });
    return out;
  }, [config, policyGroups, resultData]);

  // The cards of the open service: assessed settings by status, then the policies by type.
  const view = useMemo(() => {
    if (!config) return { setCards: [], polCards: [], nSet: 0, nPol: 0 };
    const settingRows = sections
      .filter((s) => s.settings.length > 0 && SERVICE_OF_AREA[s.area] === pArea && s.area !== CA_AREA)
      .flatMap((s) => s.settings.map((r) => ({ ...r, area: s.area })));
    const setCards = [['bad', 'Skal rettes', 'bad'], ['warn', 'Bør ses på', 'warn'], ['ok', 'I orden', 'ok'], ['neutral', 'Oplysninger', 'other']]
      .map(([tone, title, cls]) => {
        const rows = settingRows.filter((r) => (rated(r.tone) ? r.tone : 'neutral') === tone);
        return { key: `${pArea}:set:${tone}`, kind: 'set', tone: cls, title, rows, pill: { tone: cls === 'other' ? 'neutral' : cls, text: countText(rows.length, NOUN.set) }, defaultFolded: FOLDED_BY_DEFAULT.includes(tone) };
      })
      .filter((c) => c.rows.length > 0);
    const polCards = [];
    const caRows = pArea === 'entra' ? (sections.find((s) => s.area === CA_AREA)?.settings || []).map((r) => ({ ...r, area: CA_AREA })) : [];
    if (caRows.length > 0) {
      const sorted = [...caRows].sort((a, b) => ((a.tone === 'bad' ? 0 : a.tone === 'warn' ? 1 : 2) - (b.tone === 'bad' ? 0 : b.tone === 'warn' ? 1 : 2)));
      polCards.push({ key: `${pArea}:ca`, kind: 'ca', tone: 'other', title: 'Loginregler (Conditional Access)', rows: sorted, pill: { tone: 'neutral', text: countText(sorted.length, NOUN.ca) }, defaultFolded: false });
    }
    policyTypes.forEach((t) => {
      // The colour of the card is the worst of its policies: red when devices fail one badly, orange when a few do, green when every
      // policy is in order, grey when nothing is known. Red and orange come first.
      const marks = t.rows.map((r) => policyMark(r.item, r.res));
      const nBad = marks.filter((m) => m === 'notFound').length;
      const nWarn = marks.filter((m) => m === 'partial').length;
      const cls = nBad > 0 ? 'bad' : nWarn > 0 ? 'warn' : marks.length > 0 && marks.every((m) => m === 'covered') ? 'ok' : 'other';
      const rows = t.rows.map((r) => ({ id: r.id, kind: 'policy', title: policyTitle(r.item), sub: policySub(r.item), area: areaOfCategory(t.category), item: r.item, res: r.res }));
      polCards.push({ key: `${pArea}:pol:${t.key}`, kind: 'pol', tone: cls, title: t.label, rows, pill: cls === 'bad' || cls === 'warn' ? { tone: cls, text: `${nBad + nWarn} af ${rows.length} ${cls === 'bad' ? 'skal rettes' : 'bør ses på'}` } : { tone: cls === 'ok' ? 'ok' : 'neutral', text: countText(rows.length, NOUN.pol) }, defaultFolded: false });
    });
    const rank = { bad: 0, warn: 1, ok: 2, other: 3 };
    const firstPol = polCards.findIndex((c) => c.kind === 'pol');
    if (firstPol >= 0) polCards.splice(firstPol, polCards.length - firstPol, ...polCards.slice(firstPol).map((c, i) => [c, i]).sort(([a, i], [b, j]) => (rank[a.tone] - rank[b.tone]) || i - j).map(([c]) => c));
    return { setCards, polCards, nSet: settingRows.length, nPol: polCards.reduce((n, c) => n + c.rows.length, 0) };
  }, [config, sections, pArea, policyTypes]);
  const allCards = useMemo(() => [...view.setCards, ...view.polCards], [view]);

  const chosen = (selId && rowsById.get(selId)) || null;
  const selCard = (selId && allCards.find((c) => c.rows.some((r) => r.id === selId))) || null;
  // Searching or filtering by status opens every matching card — otherwise what you asked for would be hiding
  // behind a folded heading.
  const forceOpen = !!q || status !== 'all';
  const isFolded = (c) => !forceOpen && (c.key in fold ? fold[c.key] : c.defaultFolded);
  const allOpen = allCards.every((c) => !isFolded(c));

  // ?area=… (from a documented decision's "Åbn i Konfiguration") opens the service and its cards, and scrolls to the first
  // row of that area once it is drawn — landing on a folded card with nothing visible would defeat the link.
  const wantedArea = searchParams.get('area');
  useEffect(() => {
    if (!config || !wantedArea || !CONFIG_AREAS.includes(wantedArea)) return;
    if (SERVICE_OF_AREA[wantedArea]) setPArea(SERVICE_OF_AREA[wantedArea]);
    setFold((prev) => {
      const next = { ...prev };
      ['bad', 'warn', 'ok', 'neutral'].forEach((tone) => { next[`${SERVICE_OF_AREA[wantedArea] || 'intune'}:set:${tone}`] = false; });
      return next;
    });
    setTimeout(() => document.querySelector(`[data-area="${wantedArea}"]`)?.scrollIntoView({ block: 'center' }), 50);
  }, [config, wantedArea]);

  // ?policy=pol:category:id (from Compliance's "Åbn politikken i Konfiguration" and Gennemgang's "Medlemskab og politikker") goes
  // straight to that policy: its service, its card (opened, with every row shown), the policy chosen in the side panel, and the row
  // scrolled into view. It happens once per link, so searching and clicking on afterwards is never overridden.
  const wantedPolicy = searchParams.get('policy');
  const openedPolicy = useRef(null);
  useEffect(() => {
    if (!wantedPolicy || openedPolicy.current === wantedPolicy) return;
    const [, , ...rest] = wantedPolicy.split(':');
    const itemId = rest.join(':');
    // The exact id first; failing that the same policy under another category (an id is a Microsoft GUID, so it is the same policy).
    const id = rowsById.has(wantedPolicy) ? wantedPolicy : [...rowsById.keys()].find((k) => k.startsWith('pol:') && k.endsWith(`:${itemId}`));
    if (!id) return;
    const group = allPolicyGroups.find((g) => g.items.some((it) => `pol:${g.category}:${it.id}` === id));
    if (!group) return;
    openedPolicy.current = wantedPolicy;
    const cardKey = `${group.source}:pol:${group.gkey}`;
    setSelId(id);
    setPArea(group.source);
    setSecTab('policies');
    setFold((prev) => ({ ...prev, [cardKey]: false }));
    setShowAll((prev) => new Set(prev).add(cardKey));
    setTimeout(() => document.querySelector(`[data-rowid="${id.replace(/"/g, '')}"]`)?.scrollIntoView({ block: 'center' }), 120);
  }, [wantedPolicy, rowsById, allPolicyGroups]);

  // Pressing anywhere that is not a card, the side panel or a window ends the focus.
  useEffect(() => {
    if (!focusOn || !selId) return undefined;
    function onDown(e) {
      if (e.target.closest && e.target.closest('.wqc, .detail-panel-col, .wqm, .dp__pop, .op-pop, .wq-focus')) return;
      setSelId(null);
      setPicked(new Set());
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [focusOn, selId]);

  if (loading || !tenant || !config) return <LoadingState />;

  const groups = inv.data && !inv.data.error ? inv.data.groups : null;
  const policyAreaInfo = SOURCE_BY_KEY[pArea];
  const toggleAll = () => setFold(Object.fromEntries(allCards.map((c) => [c.key, allOpen])));
  // Choosing another service must also clear the selection, or the side panel would keep showing something that is not in the list.
  const onService = (key) => { setPArea(key); setSelId(null); setPicked(new Set()); };
  const pickRow = (card, row) => { setSelId(row.id); if (selCard?.key !== card.key) setPicked(new Set()); };
  const activateCard = (card) => { if (selCard?.key !== card.key) { setSelId(card.rows[0]?.id || null); setPicked(new Set()); } };
  const tickRow = (card, row) => {
    if (selCard?.key !== card.key) { setSelId(row.id); setPicked(new Set([row.id])); return; }
    setPicked((prev) => { const next = new Set(prev); if (next.has(row.id)) next.delete(row.id); else next.add(row.id); return next; });
  };
  const tiles = POLICY_SOURCES.map((s) => {
    const att = tileStatus[s.key];
    const style = SERVICE_STYLE[s.key];
    const off = s.key === 'purview';
    const text = off ? { text: 'På vej', tone: 'mute' }
      : att.bad > 0 ? { text: `${att.bad} kræver handling`, tone: 'bad' }
        : att.warn > 0 ? { text: `${att.warn} bør gennemgås`, tone: 'warn' }
          : att.ok > 0 ? { text: 'I orden', tone: 'ok' } : { text: 'Ingen vurdering endnu', tone: 'mute' };
    return {
      key: s.key,
      name: s.label,
      bg: style.bg,
      fg: style.fg,
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{style.icon}</svg>,
      status: text,
      active: pArea === s.key,
      off,
      onClick: () => onService(s.key),
    };
  });

  const renderRow = (card, r) => {
    const isPolicy = r.kind === 'policy';
    const note = decided.get(r.id);
    const ticked = selCard?.key === card.key && picked.has(r.id);
    const current = selCard?.key === card.key && (picked.size > 0 ? ticked : r.id === selId);
    const tone = !isPolicy && rated(r.tone) ? r.tone : null;
    let value = r.value;
    let valueTone = null;
    if (r.ca) { value = r.ca.state === 'enabled' ? caRequires(r.ca) : (CA_STATE_TEXT[r.ca.state] || r.value); valueTone = r.ca.state === 'enabled' ? null : 'warn'; }
    if (isPolicy) {
      value = !isAssigned(r.item) ? 'Ikke tildelt' : r.res && r.res.total > 0 ? `${r.res.ok} af ${r.res.total} enheder` : '';
      valueTone = r.res && r.res.problems > 0 ? (r.res.problems / r.res.total > 0.15 ? 'bad' : 'warn') : null;
    }
    // The type is already said by the card and the section, so the column only holds what tells the rows apart.
    const sub = isPolicy ? (r.sub && r.sub !== '—' ? r.sub : '') : r.ca ? `Rammer: ${r.ca.targetSummaryShort || r.ca.includeUsers || 'Alle brugere'}` : (AREA_LABEL[r.area] || r.area);
    const excluded = r.ca ? (r.ca.excludeSummaryShort || r.ca.excludeUsers || null) : null;
    return (
      <div key={r.id} data-area={r.area} data-rowid={r.id} className={`wqc__row${current ? ' is-sel' : ''}${note?.decisionType ? ' is-done' : ''}`} onClick={(e) => { e.stopPropagation(); pickRow(card, r); }}>
        <label className="wqc__pick" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={ticked} onChange={() => tickRow(card, r)} aria-label={`Vælg ${r.title}`} />
          <span className="wqc__cb" aria-hidden="true"><PortalIcon name="check" size={11} /></span>
        </label>
        <span className={`wqc__ic${tone ? ` wqc__ic--${tone}` : ''}`}><NcIcon name={isPolicy ? 'clipboard' : (AREA_ICON[r.area] || 'grid')} size={15} /></span>
        <span className="wqc__nm">
          <span className="wqc__l1"><b title={r.title}>{r.title}</b></span>
        </span>
        <span className={`wqc__meta${r.ca ? ' wqc__meta--ca' : ''}`} title={excluded ? `${sub}. Undtager: ${excluded}` : sub}>{sub}{excluded && <em className="wqc__excl">Undtager: {excluded}</em>}</span>
        <span className={`wqc__val${valueTone ? ` wqc__val--${valueTone}` : ''}`} title={value}>{value}</span>
        <span className="wqc__tags">
          {itList.has(itKeyOf('cfg', card.key), String(r.id).slice(0, 200)) && <span className="itl-mark" title="På IT-opgaver"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>IT</span>}
          {note && <DecisionTag badge={getStatusBadge(note)} />}
        </span>
      </div>
    );
  };

  const renderCard = (c) => {
    const folded = isFolded(c);
    const all = showAll.has(c.key);
    const shown = all ? c.rows : c.rows.slice(0, FUND_ROWS);
    return (
      <StatusCard
        key={c.key}
        tone={c.tone}
        title={c.title}
        pill={c.pill}
        selected={selCard?.key === c.key}
        folded={folded}
        onFold={() => setFold((prev) => ({ ...prev, [c.key]: !folded }))}
        onActivate={() => activateCard(c)}
        rows={shown.map((r) => renderRow(c, r))}
        total={c.rows.length}
        hidden={c.rows.length - shown.length}
        onShowAll={() => setShowAll((prev) => new Set(prev).add(c.key))}
        {...cardItProps({ prefix: 'cfg', areaName: 'Konfiguration', card: c, selCard, picked, selId, setPicked, activateCard, itList })}
      />
    );
  };

  const emptyFor = (what) => (inv.loading ? 'Henter…' : q || status !== 'all' ? `Ingen ${what} matcher filteret.` : null);

  return (
    <div className={`arbejdsko-page cfg2${focusOn ? ' is-focus' : ''}`} ref={pageRef} style={{ '--pw': `${detailPanel.width}px`, ...toolsStyle }}>
      <div className="head">
        <div>
          <div className="eye">Konfiguration</div>
          <h1>Sådan er jeres Microsoft 365 sat op</h1>
          <p>Det, NordComply har læst og vurderet, og de regler I selv har sat op. Vælg et område for at se detaljerne.</p>
        </div>
        <div className="cfg2__tools">
          <FocusToggle on={focusOn} onToggle={toggleFocus} />
        </div>
      </div>

      <div className="workspace">
        <div className="list-panel list-panel--float wq-menu">
          <div className="wq-menu__card mc-frame">
            <TileMenu tiles={tiles} label="Microsoft-service" />
            <div className="list-panel__filter">
              <div className="wq-seg" role="group" aria-label="Status">
                {STATUS_FILTERS.map(([key, name]) => (
                  <button key={key} type="button" className={`wq-seg__btn${status === key ? ' is-on' : ''}`} onClick={() => setStatus(key)}>
                    {key !== 'all' && <span className={`wq-seg__dot wq-seg__dot--${key}`} />}{name}
                  </button>
                ))}
              </div>
              <div className="wq-menu__right">
                {!forceOpen && allCards.length > 0 && <ExpandAll allOpen={allOpen} onClick={toggleAll} />}
                <SearchInput value={search} onChange={setSearch} placeholder="Søg indstilling eller politik..." />
              </div>
            </div>
          </div>
          <div className="list-panel__gap" />
          <div className={`list-panel__body wq-tray wq-tray--cards${focusOn && chosen ? ' has-sel' : ''}`}>
            {pArea === 'purview' && (
              <div className="state-empty"><p><b>Ikke tilgængeligt endnu</b><br />NordComply læser hverken indstillinger eller politikker fra Purview (DLP, følsomhedsetiketter og opbevaring) endnu. Den er på vej.</p></div>
            )}
            {pArea !== 'purview' && (
              <>
                {(() => {
                  // The tab in use: the one with content when the other is empty.
                  const tab = view.nSet === 0 && view.nPol > 0 ? 'policies' : view.nPol === 0 ? 'settings' : secTab;
                  return (
                    <>
                      <div className="cfg2__tabs" role="tablist">
                        <button type="button" role="tab" aria-selected={tab === 'settings'} className={tab === 'settings' ? 'is-on' : ''} onClick={() => setSecTab('settings')}>Indstillinger <i>{view.nSet}</i></button>
                        <button type="button" role="tab" aria-selected={tab === 'policies'} className={tab === 'policies' ? 'is-on' : ''} onClick={() => setSecTab('policies')}>Politikker <i>{view.nPol}</i></button>
                      </div>
                      <p className="cfg2__tabdesc">{tab === 'settings' ? 'Sådan er jeres Microsoft 365 sat op, vurderet mod anbefalet praksis.' : 'Tenant-specifikke politikker, udviklet af jer eller jeres IT-leverandør, fx sikkerhedspolitikker i Intune som del af en baseline. Vi vurderer ikke selve reglerne.'}</p>
                      {tab === 'settings' ? (
                        <>
                          {view.setCards.length === 0 && <p className="cfg-none">{emptyFor('indstillinger') || 'NordComply læser ingen indstillinger herfra endnu.'}</p>}
                          {view.setCards.map(renderCard)}
                        </>
                      ) : (
                        <>
                          {view.polCards.length === 0 && <p className="cfg-none">{emptyFor('politikker') || `NordComply finder ingen politikker i ${policyAreaInfo.label}.`}</p>}
                          {/* One frame per status, red first: the policy types of that status sit inside it. */}
                          {[['bad', 'Kræver handling'], ['warn', 'Bør gennemgås'], ['ok', 'I orden'], ['other', 'Øvrige']].map(([tone, title]) => {
                            const cs = view.polCards.filter((c) => c.tone === tone);
                            if (cs.length === 0) return null;
                            return (
                              <StatusFrame key={tone} tone={tone === 'other' ? 'neutral' : tone} title={title} count={cs.reduce((n, c) => n + c.rows.length, 0)} folded={frameFold.has(tone)} onToggle={() => setFrameFold((prev) => { const next = new Set(prev); if (next.has(tone)) next.delete(tone); else next.add(tone); return next; })}>
                                {cs.map(renderCard)}
                              </StatusFrame>
                            );
                          })}
                        </>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        <div className={`resize-handle${detailPanel.dragging ? ' is-dragging' : ''}`} onMouseDown={detailPanel.onMouseDown} />

        <div className="detail-panel-col" style={{ width: detailPanel.width }}>
          <div className="detail-panel">
            {chosen
              ? <RowPanel key={chosen.row.id} row={chosen.row} area={chosen.area} tenant={tenant} groups={groups} results={results} evidenceFor={wantedPolicy && chosen.row.id.endsWith(':' + wantedPolicy.split(':').slice(2).join(':')) ? searchParams.get('evidence') : null} note={decided.get(chosen.row.id)} />
              : (
                <div className="wq-empty">
                  <div className="wq-empty__slot">
                    <span className="wq-empty__ic" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg></span>
                    <b>Vælg en indstilling eller politik</b>
                    <small>Forklaring, anbefaling og fakta vises her.</small>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
